import { useEffect, useRef, useState } from 'react';
import { Check, X } from 'lucide-react';
import { BotanicalAccent } from '@/components/ui/botanical-accent';
import { ScrollReveal } from '@/components/ui/scroll-reveal';
import { SplitText } from '@/components/ui/split-text';
import { useLang } from '@/lib/LanguageContext';
import { cn } from '@/lib/utils';

type Lang = 'fr' | 'en';

interface Scenario {
  scenario: { fr: string; en: string };
  collectif: { fr: string; en: string; tone: 'bad' | 'partial' | 'good' };
  amg: { fr: string; en: string; tone: 'bad' | 'partial' | 'good' };
}

const SCENARIOS: Scenario[] = [
  {
    scenario: {
      fr: 'Cancer diagnostiqué (sans invalidité immédiate)',
      en: 'Cancer diagnosed (no immediate disability)'
    },
    collectif: { fr: '0 $ versé', en: '$0 paid out', tone: 'bad' },
    amg: { fr: '100K à 500K $ versés', en: '$100K to $500K paid out', tone: 'good' }
  },
  {
    // 2026-06-11 — « chimio expérimentale » trompeur (essais cliniques = gratuits ;
    // RAMQ universelle au QC) → recadré sur les dépenses hors-RAMQ réelles
    // (Longo 2021 : 518 $/mois en moyenne pendant le traitement).
    scenario: {
      fr: 'Dépenses que la RAMQ ne couvre pas (médicaments, soins, frais)',
      en: 'Expenses RAMQ doesn\'t cover (medication, care, fees)'
    },
    collectif: { fr: 'Partiellement couvert', en: 'Partially covered', tone: 'partial' },
    amg: { fr: 'Capital libre d\'usage', en: 'Capital used freely', tone: 'good' }
  },
  {
    scenario: {
      fr: 'Hypothèque à payer pendant le traitement',
      en: 'Mortgage to pay during treatment'
    },
    collectif: { fr: '0 $ — couvre 60 % du salaire', en: '$0 — covers 60% of salary', tone: 'bad' },
    amg: { fr: 'Capital peut éponger la dette', en: 'Capital can wipe the debt', tone: 'good' }
  },
  {
    scenario: {
      fr: 'Conjoint qui prend congé pour aider',
      en: 'Partner takes leave to help'
    },
    collectif: { fr: 'Non couvert', en: 'Not covered', tone: 'bad' },
    amg: { fr: 'Couvert via le capital', en: 'Covered via capital', tone: 'good' }
  },
  {
    scenario: {
      fr: 'Transport vers centre oncologique éloigné',
      en: 'Transport to distant oncology center'
    },
    collectif: { fr: 'Non couvert', en: 'Not covered', tone: 'bad' },
    amg: { fr: 'Couvert (vous décidez)', en: 'Covered (your call)', tone: 'good' }
  },
  {
    scenario: {
      fr: 'Si vous quittez votre employeur',
      en: 'If you leave your employer'
    },
    collectif: { fr: 'Couverture disparaît', en: 'Coverage disappears', tone: 'bad' },
    amg: { fr: 'Couverture vous suit', en: 'Coverage stays with you', tone: 'good' }
  },
  {
    scenario: {
      fr: 'Diagnostic après 2 ans de cotisation',
      en: 'Diagnosis after 2 years of contributions'
    },
    collectif: { fr: 'Dépend du contrat', en: 'Contract-dependent', tone: 'partial' },
    amg: { fr: 'Plein capital versé', en: 'Full capital paid out', tone: 'good' }
  }
];

const UI = {
  eyebrow: { fr: 'Comparateur', en: 'Side-by-side' },
  title: {
    fr: 'Régime collectif vs assurance maladie grave.',
    en: 'Group plan vs critical illness insurance.'
  },
  subtitle: {
    fr: '7 situations concrètes, deux protections comparées. Pas un argumentaire — un constat.',
    en: '7 real situations, two protections compared. Not a sales pitch — a finding.'
  },
  scenario: { fr: 'Situation', en: 'Situation' },
  collectif: { fr: 'Régime collectif', en: 'Group plan' },
  amg: { fr: 'AMG (Océanne)', en: 'AMG (Océanne)' },
  legend_bad: { fr: 'Non protégé', en: 'Not protected' },
  legend_partial: { fr: 'Partiellement', en: 'Partial' },
  legend_good: { fr: 'Protégé', en: 'Protected' }
};

function getText<T extends { fr: string; en: string }>(obj: T, lang: Lang): string {
  return obj[lang];
}

function ToneIcon({ tone, className }: { tone: 'bad' | 'partial' | 'good'; className?: string }) {
  if (tone === 'good') {
    return <Check className={cn('text-secondary', className)} strokeWidth={2.5} aria-hidden="true" />;
  }
  if (tone === 'bad') {
    return <X className={cn('text-primary', className)} strokeWidth={2.5} aria-hidden="true" />;
  }
  return (
    <span
      className={cn(
        'flex h-4 w-4 items-center justify-center rounded-full bg-tertiary text-foreground',
        className
      )}
      aria-hidden="true"
    >
      <span className="text-[10px] font-bold">~</span>
    </span>
  );
}

export function ComparatorCollectifVsAMG() {
  const { lang } = useLang();
  const sectionRef = useRef<HTMLDivElement>(null);
  // Phase 27.72D — lazy init : reduced-motion users sautent l'IntersectionObserver
  // (visible direct au mount). Sinon, init false + Observer prend le relais.
  const [visible, setVisible] = useState(
    () =>
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );

  useEffect(() => {
    if (visible) return; // déjà visible (reduced-motion lazy init), pas besoin de l'observer
    if (typeof window === 'undefined') return;
    const node = sectionRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setVisible(true);
            observer.disconnect();
          }
        }
      },
      { threshold: 0.2 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [visible]);

  return (
    <section id="comparateur" className="section-cv-auto atmosphere-watercolor-terracotta surface-cream relative w-full overflow-hidden py-20 md:py-32">
      <BotanicalAccent variant="leaf-sage" position="top-left" size="md" opacity={0.12} />
      {/* Phase 27.19 — combo Q.2 : second leaf-sage top-right for warm earthy balance */}
      <BotanicalAccent variant="leaf-sage" position="top-right" size="md" opacity={0.18} />
      <div ref={sectionRef} className="relative z-[1] mx-auto max-w-[1440px] px-5 md:px-20">
        {/* Header */}
        <div className="mb-12 text-center md:mb-16">
          <ScrollReveal as="span" className="eyebrow-pill mb-6">
            {getText(UI.eyebrow, lang)}
          </ScrollReveal>
          <SplitText as="h2" className="mx-auto mb-6 max-w-3xl font-display text-4xl leading-[1.15] text-balance text-foreground md:text-5xl">
            {getText(UI.title, lang)}
          </SplitText>
          <ScrollReveal delay={200} as="p" className="mx-auto max-w-2xl font-sans text-base text-pretty hyphens-auto text-muted-foreground">
            {getText(UI.subtitle, lang)}
          </ScrollReveal>
        </div>

        {/* Comparator table */}
        <ScrollReveal delay={300} className="overflow-hidden rounded-2xl">
          {/* Headers */}
          <div className="grid grid-cols-[1.5fr_1fr_1fr] gap-px bg-border md:grid-cols-[2fr_1fr_1fr]">
            <div className="bg-background px-4 py-5 font-sans text-xs font-bold uppercase tracking-widest text-foreground md:px-8 md:py-6">
              {getText(UI.scenario, lang)}
            </div>
            <div className="surface-cream-deep flex items-center justify-center px-4 py-5 font-display text-sm italic text-foreground md:px-8 md:py-6 md:text-base">
              {getText(UI.collectif, lang)}
            </div>
            <div className="flex items-center justify-center bg-primary/10 px-4 py-5 font-display text-sm italic text-primary md:px-8 md:py-6 md:text-base">
              {getText(UI.amg, lang)}
            </div>
          </div>

          {/* Rows */}
          {SCENARIOS.map((row, i) => (
            <div
              key={i}
              className="group grid grid-cols-[1.5fr_1fr_1fr] gap-px bg-border md:grid-cols-[2fr_1fr_1fr]"
              style={{
                opacity: visible ? 1 : 0,
                transform: visible ? 'translateY(0)' : 'translateY(20px)',
                transition: `opacity 600ms var(--ease-out-expo) ${i * 80}ms, transform 600ms var(--ease-out-expo) ${i * 80}ms`
              }}
            >
              {/* Scenario — Phase 27.18 combo J.5 hover wash */}
              <div className="bg-background px-4 py-4 transition-colors duration-300 group-hover:bg-rose-blush/8 md:px-8 md:py-5">
                <span className="font-sans text-sm leading-snug text-foreground md:text-base">
                  {getText(row.scenario, lang)}
                </span>
              </div>

              {/* Collectif */}
              <div className="surface-cream-deep flex items-center gap-2 px-4 py-4 transition-colors duration-300 group-hover:bg-rose-blush/8 md:gap-3 md:px-8 md:py-5">
                <ToneIcon tone={row.collectif.tone} className="h-4 w-4 shrink-0 md:h-5 md:w-5" />
                <span className="font-sans text-xs leading-snug text-foreground/80 md:text-sm">
                  {getText(row.collectif, lang)}
                </span>
              </div>

              {/* AMG */}
              <div className="flex items-center gap-2 bg-primary/5 px-4 py-4 transition-colors duration-300 group-hover:bg-rose-blush/8 md:gap-3 md:px-8 md:py-5">
                <ToneIcon tone={row.amg.tone} className="h-4 w-4 shrink-0 md:h-5 md:w-5" />
                <span className="font-sans text-xs leading-snug text-foreground md:text-sm">
                  {getText(row.amg, lang)}
                </span>
              </div>
            </div>
          ))}
        </ScrollReveal>

        {/* Legend */}
        <ScrollReveal delay={400} className="mt-8 flex flex-wrap justify-center gap-6 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-2">
            <Check className="h-4 w-4 text-secondary" strokeWidth={2.5} aria-hidden="true" />
            {getText(UI.legend_good, lang)}
          </span>
          <span className="inline-flex items-center gap-2">
            <span className="flex h-4 w-4 items-center justify-center rounded-full bg-tertiary">
              <span className="text-[10px] font-bold text-foreground">~</span>
            </span>
            {getText(UI.legend_partial, lang)}
          </span>
          <span className="inline-flex items-center gap-2">
            <X className="h-4 w-4 text-primary" strokeWidth={2.5} aria-hidden="true" />
            {getText(UI.legend_bad, lang)}
          </span>
        </ScrollReveal>
      </div>
      {/* Phase 27.18 — sunset rose-blush gradient bottom (combo I.3) */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 bottom-0 z-0 h-16 md:h-20 bg-gradient-to-t from-rose-blush/30 to-transparent"
      />
    </section>
  );
}
